import { useLingui } from '@lingui/react/macro';
import { FaqAccordion, type FaqItem } from '../faq-accordion';
import { DiamondGrid, diamondGlows } from '../primitives';

/**
 * The page's four questions.
 *
 * Q1's answer is the designer's own, taken from the `pregunta` component in the UI Kit (`45:814`),
 * which is the one item the hand-off draws open. The other three are drawn CLOSED, so the design
 * specifies their questions and none of their answers — those are drafted from how the process
 * actually works and are marked for the content owner to approve (TEC-3626).
 *
 * A hook rather than a module constant because the strings are translated, and it is exported so the
 * section and the page's `FAQPage` structured data are built from ONE array. Answering a question in
 * markup that the page does not show, or vice versa, is exactly the drift that gets a rich result
 * pulled.
 */
export function useFaqItems(): FaqItem[] {
	const { t } = useLingui();
	return [
		{
			question: t`What requirements does Worldcoo set for an NGO that wants to join our partner entities?`,
			answer: t`To join Worldcoo, an organisation has to meet all of the following:`,
			bullets: [
				t`Be recognised as a non-profit organisation.`,
				t`Issue donation certificates valid for tax relief.`,
				t`A track record of at least 3 years since it was constituted.`,
				t`An up-to-date, professional website.`,
				t`Descriptive annual reports on the website.`,
				t`Information about the board of directors or trustees.`,
				t`Publicly available contact details.`,
				t`A data protection policy that complies with current regulations.`,
				t`A presence on social media.`,
				t`Respect for the principles of the Charter of the United Nations.`,
			],
		},
		{
			question: t`How does the project selection process work?`,
			answer: t`Approved projects join our database, and each partner company chooses the ones it wants to fund according to its own values and social strategy. Selection is therefore made by the company, not by Worldcoo — our job is to make sure every project in the pool meets the transparency criteria.`,
		},
		{
			question: t`Once a project is funded, how are the funds raised managed?`,
			answer: t`Donations are collected by the partner company and settled to the NGO through Worldcoo on a regular cycle. You can follow what has been raised in real time from your NGO area, and once the project reaches its goal we transfer the amount and ask for the report showing how it was spent.`,
		},
		{
			question: t`Commitments of the beneficiary organisations`,
			answer: t`Beneficiary entities undertake to use the funds only for the funded project, to report on how they were spent and on the results achieved, and to provide photographs and updates so the people who donated can see the impact of their contribution.`,
		},
	];
}

/**
 * "Preguntas frecuentes" — the linen band that closes the page before the footer CTA.
 *
 * The two diamond-textured glows are the hand-off's own, one bleeding off each side; geometry comes
 * from `diamondGlows`, which encodes the 777x501 ellipse the whole design system builds them from.
 */
export function Faq() {
	const { t } = useLingui();
	const items = useFaqItems();

	return (
		<section className="bg-linen relative overflow-hidden">
			<DiamondGrid
				color="var(--color-brand)"
				opacity={0.35}
				masks={[diamondGlows({ x: '6%', y: '0%' }, { x: '94%', y: '78%' })]}
			/>

			<div className="relative mx-auto flex max-w-[1440px] flex-col items-center gap-8 px-6 py-16 md:py-[104px]">
				<h2 className="text-center text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
					{t`Frequently asked questions`}
				</h2>

				<FaqAccordion items={items} />
			</div>
		</section>
	);
}
